// Active-club state for the whole authenticated app.
//
// Before multi-tenancy every page independently fetched /v1/me and used
// `memberships[0]`, which quietly picked a club for anyone who belongs to more
// than one (common: a coach at both a club and a high school). This provider
// fetches /v1/me once, remembers which club the user last chose, and hands
// every page the same answer.
//
// The chosen club is a UI preference, never an authorization decision — the
// API re-checks membership on every request. Persisting it in localStorage is
// therefore safe even though localStorage is user-writable: the worst a tampered
// value achieves is a 403.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, type ClubMembership, type MeResponse } from "./api";

const ACTIVE_CLUB_KEY = "clubmanager_active_club";

interface ClubContextValue {
  me: MeResponse | null;
  clubs: ClubMembership[];
  activeClub: ClubMembership | null;
  setActiveClubId: (clubId: string) => void;
  /** Convenience: is the user an admin *of the active club*? */
  isActiveClubAdmin: boolean;
  isSuperAdmin: boolean;
  /**
   * Re-fetch /v1/me. Needed because a super admin's club list changes from
   * inside the app: create a club on the Clubs page and it should appear in
   * the switcher without a full reload.
   */
  refresh: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const ClubContext = createContext<ClubContextValue | null>(null);

export function ClubProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [activeClubId, setActiveClubIdState] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_CLUB_KEY)
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Guards against setting state after unmount — the fetch outlives a fast
  // navigation away. A ref rather than the usual per-effect `cancelled` flag
  // because `load` is also called on demand by refresh(), long after mount.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api<MeResponse>("/v1/me");
      if (!alive.current) return;
      setMe(res);
      // A remembered id is only usable if it's still a club the user can
      // reach — memberships get revoked, and localStorage outlives them.
      setActiveClubIdState((current) => {
        const stillValid = current && res.memberships.some((m) => m.clubId === current);
        return stillValid ? current : res.memberships[0]?.clubId ?? null;
      });
      setError(null);
    } catch (e) {
      if (!alive.current) return;
      setError(e instanceof Error ? e.message : "Failed to load your clubs");
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setActiveClubId = useCallback((clubId: string) => {
    localStorage.setItem(ACTIVE_CLUB_KEY, clubId);
    setActiveClubIdState(clubId);
  }, []);

  const value = useMemo<ClubContextValue>(() => {
    const clubs = me?.memberships ?? [];
    const activeClub = clubs.find((c) => c.clubId === activeClubId) ?? null;
    return {
      me,
      clubs,
      activeClub,
      setActiveClubId,
      isActiveClubAdmin: activeClub?.role === "admin",
      isSuperAdmin: me?.isSuperAdmin ?? false,
      refresh: load,
      loading,
      error,
    };
  }, [me, activeClubId, setActiveClubId, load, loading, error]);

  return <ClubContext.Provider value={value}>{children}</ClubContext.Provider>;
}

export function useClubs(): ClubContextValue {
  const ctx = useContext(ClubContext);
  if (!ctx) throw new Error("useClubs must be used inside a ClubProvider");
  return ctx;
}
